import React, { useState, useEffect, useMemo } from 'react';
import InputField from '../../../components/InputField/InputField';
import Button from '../../../components/Button/Button';
import styles from './Profile.module.css';
import { useUserProfile } from '../../../hooks/useUserProfile';
import { useToast } from '../../../contexts/ToastContext';
import useNavigationPrompt from '../../../hooks/useNavigationPrompt';
import spinnerStyles from '../../../components/Spinner/spinner.module.css';
import { getApiErrorMessage } from '../../../types/api-errors';
import { useNavigate } from 'react-router-dom';
import { getPasswordStrength, strengthToColor } from '../../../lib/passwordStrength';

/** Strip leading '+' and known country code prefix from a full phone number. */
const splitPhoneNumber = (fullPhone: string, storedCountryCode: string): { countryCode: string; localNumber: string } => {
    if (!fullPhone) return { countryCode: storedCountryCode || '', localNumber: '' };

    // Normalise: remove any leading '+' for comparison
    let digits = fullPhone.replace(/^\+/, '');
    let code = storedCountryCode ? storedCountryCode.replace(/^\+/, '') : '';

    // If the digits start with the country code, strip it to get the local part
    if (code && digits.startsWith(code)) {
        const local = digits.slice(code.length);
        return { countryCode: `+${code}`, localNumber: local };
    }

    // Fallback: return whatever we have
    return { countryCode: storedCountryCode ? `+${code}` : '', localNumber: digits };
};

/** Sanitise text: reject characters that enable XSS (<, >, &) */
const containsXssChars = (value: string): boolean => /[<>&]/.test(value);

const Profile: React.FC = () => {
    const { profile, loadProfile, updateProfile, changePassword, loading } = useUserProfile();
    const [isEditing, setIsEditing] = useState(false);
    const [form, setForm] = useState({ fullName: '', email: '', phoneNumber: '', countryCode: '' });
    const [saving, setSaving] = useState(false);
    const [changePwMode, setChangePwMode] = useState(false);
    const [pw, setPw] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
    const [pwSaving, setPwSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [hasUnsavedPasswordChanges, setHasUnsavedPasswordChanges] = useState(false);
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [pwTouched, setPwTouched] = useState<Record<string, boolean>>({});

    const { showToast } = useToast();
    const navigate = useNavigate();

    // #1158 / #1213 — properly split phone number into country code + local number
    useEffect(() => {
        if (profile) {
            const { countryCode, localNumber } = splitPhoneNumber(
                profile.phoneNumber || '',
                profile.countryCode || ''
            );
            setForm({
                fullName: profile.fullName || '',
                email: profile.email || '',
                phoneNumber: localNumber,
                countryCode: countryCode,
            });
            setTouched({});
        }
    }, [profile]);

    useEffect(() => {
        if (!profile) loadProfile().catch((err) => {
            const status = err?.response?.status;
            if (status === 401) {
                showToast('Your session has expired. Please log in again.', 'info');
                navigate('/form/login');
            } else {
                showToast('Unable to load profile information. Please refresh the page or try again later. If the issue persists, contact support.', 'error');
            }
        });
    }, [profile, loadProfile, navigate, showToast]);

    // Derive the "saved" form values from profile for dirty comparison
    const savedForm = useMemo(() => {
        if (!profile) return { fullName: '', email: '', phoneNumber: '', countryCode: '' };
        const { countryCode, localNumber } = splitPhoneNumber(
            profile.phoneNumber || '',
            profile.countryCode || ''
        );
        return {
            fullName: profile.fullName || '',
            email: profile.email || '',
            phoneNumber: localNumber,
            countryCode: countryCode,
        };
    }, [profile]);

    // #1228 — Only mark dirty when validated values actually differ from saved state
    useEffect(() => {
        if (profile && isEditing) {
            const nameValid = validateFullName(form.fullName) && !containsXssChars(form.fullName);
            const emailValid = validateEmail(form.email) && !containsXssChars(form.email);
            const phoneValid = form.phoneNumber === '' || validatePhoneNumber(form.phoneNumber);
            const codeValid = form.countryCode === '' || validateCountryCode(form.countryCode);

            const allValid = nameValid && emailValid && phoneValid && codeValid;
            const changed = form.fullName !== savedForm.fullName ||
                form.email !== savedForm.email ||
                form.phoneNumber !== savedForm.phoneNumber ||
                form.countryCode !== savedForm.countryCode;

            setHasUnsavedChanges(allValid && changed);
        } else {
            setHasUnsavedChanges(false);
        }
    }, [form, savedForm, profile, isEditing]);

    // Track unsaved changes in password form
    useEffect(() => {
        if (changePwMode) {
            const hasPasswordData = pw.currentPassword !== '' || pw.newPassword !== '' || pw.confirmPassword !== '';
            setHasUnsavedPasswordChanges(hasPasswordData);
        } else {
            setHasUnsavedPasswordChanges(false);
        }
    }, [pw, changePwMode]);

    // Warn before leaving page with unsaved changes (profile or password)
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges || hasUnsavedPasswordChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [hasUnsavedChanges, hasUnsavedPasswordChanges]);

    // Prompt for in-app navigation when there are unsaved changes
    useNavigationPrompt((hasUnsavedChanges || hasUnsavedPasswordChanges));

    const handleChange = (name: string, value: string) => {
        setForm(prev => ({ ...prev, [name]: value }));
        setTouched(prev => ({ ...prev, [name]: true }));
    };

    const handleCancel = () => {
        if (hasUnsavedChanges) {
            const confirmed = window.confirm('You have unsaved changes. Do you want to discard them?');
            if (!confirmed) return;
        }
        if (profile) {
            setForm({ ...savedForm });
        }
        setIsEditing(false);
        setHasUnsavedChanges(false);
        setTouched({});
    };

    const parseApiErrors = (err: any) => {
        try {
            const errors = err?.response?.data?.errors;
            if (errors && typeof errors === 'object') {
                const msgs = Object.values(errors).flat().join('. ');
                return msgs;
            }
            return getApiErrorMessage(err, 'An error occurred'); // AB#1919 (WS-5b)
        } catch (ex) {
            return 'An error occurred';
        }
    };

    // --- Validation functions ---

    // #1226 — validate email format (unchanged regex) but also reject XSS chars
    const validateEmail = (email: string): boolean => {
        if (containsXssChars(email)) return false;
        const emailRegex = /^[a-zA-Z0-9.!#$%'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
        return emailRegex.test(email);
    };

    // #1159 — phone regex now allows '+' prefix so international numbers work
    const validatePhoneNumber = (phone: string): boolean => {
        if (phone === '') return true; // phone is optional
        const phoneRegex = /^\+?[0-9]{5,15}$/;
        return phoneRegex.test(phone);
    };

    const validateCountryCode = (code: string): boolean => {
        if (code === '') return true; // optional
        const codeRegex = /^\+?[0-9]{1,4}$/;
        return codeRegex.test(code);
    };

    // #1191 / #1226 — full name is required (>= 2 chars) and must not contain XSS chars
    const validateFullName = (name: string): boolean => {
        if (containsXssChars(name)) return false;
        return name.trim().length >= 2 && name.length <= 50;
    };

    const isFormValid = (): boolean => {
        return validateFullName(form.fullName) &&
            validateEmail(form.email) &&
            (form.phoneNumber === '' || validatePhoneNumber(form.phoneNumber)) &&
            (form.countryCode === '' || validateCountryCode(form.countryCode));
    };

    // #1169 / #1191 / #1226 — inline field-level error messages
    const getFieldError = (field: string): string | undefined => {
        if (!touched[field]) return undefined;
        switch (field) {
            case 'fullName':
                if (!form.fullName.trim()) return 'Full Name is required.';
                if (containsXssChars(form.fullName)) return 'Full Name must not contain <, > or & characters.';
                if (form.fullName.trim().length < 2) return 'Full Name must be at least 2 characters.';
                if (form.fullName.length > 50) return 'Full Name must be at most 50 characters.';
                return undefined;
            case 'email':
                if (!form.email.trim()) return 'Email is required.';
                if (containsXssChars(form.email)) return 'Email must not contain <, > or & characters.';
                if (!validateEmail(form.email)) return 'Please enter a valid email address.';
                return undefined;
            case 'phoneNumber':
                if (form.phoneNumber && !validatePhoneNumber(form.phoneNumber)) return 'Phone number must be 5-15 digits.';
                return undefined;
            case 'countryCode':
                if (form.countryCode && !validateCountryCode(form.countryCode)) return 'Enter a valid country code (e.g. +971).';
                return undefined;
            default:
                return undefined;
        }
    };

    // Password validation: min 8, uppercase, lowercase, digit, special char
    const validatePassword = (pwd: string): boolean => {
        if (!pwd) return false;
        if (pwd.length < 8) return false;
        const hasUpperCase = /[A-Z]/.test(pwd);
        const hasLowerCase = /[a-z]/.test(pwd);
        const hasDigit = /[0-9]/.test(pwd);
        const hasSpecialChar = /[^a-zA-Z0-9]/.test(pwd);
        return hasUpperCase && hasLowerCase && hasDigit && hasSpecialChar;
    };

    // #1192 / #769 / #715 — inline field-level errors for the Change Password form
    const getPwFieldError = (field: string): string | undefined => {
        if (!pwTouched[field]) return undefined;
        switch (field) {
            case 'currentPassword':
                if (!pw.currentPassword) return 'This field is required';
                return undefined;
            case 'newPassword':
                if (!pw.newPassword) return 'This field is required';
                if (!validatePassword(pw.newPassword))
                    return 'Password must be at least 8 characters with uppercase, lowercase, digit, and special character';
                return undefined;
            case 'confirmPassword':
                if (!pw.confirmPassword) return 'This field is required';
                if (pw.newPassword !== pw.confirmPassword) return 'Passwords do not match';
                return undefined;
            default:
                return undefined;
        }
    };

    const handleSave = async () => {
        // Touch all fields to surface any remaining errors
        setTouched({ fullName: true, email: true, phoneNumber: true, countryCode: true });

        if (!isFormValid()) {
            showToast('Please fix the highlighted errors before saving.', 'error');
            return;
        }

        // #1158 — normalise country code: ensure it starts with '+' and strip '+' from local number
        const normalisedCode = form.countryCode ? (form.countryCode.startsWith('+') ? form.countryCode : `+${form.countryCode}`) : '';
        const normalisedPhone = form.phoneNumber.replace(/^\+/, '');

        setSaving(true);
        try {
            await updateProfile({ fullName: form.fullName, email: form.email, phoneNumber: normalisedPhone, countryCode: normalisedCode });
            showToast('Profile updated successfully', 'success');
            setIsEditing(false);
            setHasUnsavedChanges(false);
            setTouched({});
            await loadProfile();
        } catch (err: any) {
            const msg = parseApiErrors(err);
            showToast(msg, 'error');
        } finally {
            setSaving(false);
        }
    };

    const handlePwChange = async () => {
        // Touch all password fields to surface inline errors
        setPwTouched({ currentPassword: true, newPassword: true, confirmPassword: true });

        if (!pw.currentPassword || !validatePassword(pw.newPassword) || pw.newPassword !== pw.confirmPassword) {
            showToast('Please fix the highlighted errors before saving.', 'error');
            return;
        }

        setPwSaving(true);
        try {
            await changePassword({ currentPassword: pw.currentPassword, newPassword: pw.newPassword, confirmPassword: pw.confirmPassword });
            setPw({ currentPassword: '', newPassword: '', confirmPassword: '' });
            setChangePwMode(false);
            setHasUnsavedPasswordChanges(false);
            setPwTouched({});
            showToast('Password changed successfully', 'success');
        } catch (err: any) {
            const msg = parseApiErrors(err);
            showToast(msg, 'error');
        } finally {
            setPwSaving(false);
        }
    };

    const handleCancelPasswordChange = () => {
        if (hasUnsavedPasswordChanges) {
            const confirmed = window.confirm('You have unsaved changes. Do you want to discard them?');
            if (!confirmed) return;
        }
        setPw({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setChangePwMode(false);
        setHasUnsavedPasswordChanges(false);
        setPwTouched({});
    };

    // #1220 — styled placeholder for missing values
    const emptyPlaceholder = '\u2014';

    return (
        <div className={styles.profileCard}>
            {loading && (
                <div className={styles.cardLoading}>
                    <div className={spinnerStyles.spinner} />
                </div>
            )}

            {/* #1189 — edit form REPLACES read-only view; header only shown in read mode */}
            {!isEditing ? (
                <>
                    <div className={styles.cardHeader}>
                        <div className={styles.cardTitle}>Profile Information</div>
                        <button className={styles.editButton} onClick={() => setIsEditing(true)}>
                            <svg className={styles.editIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                <path d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                            Edit
                        </button>
                    </div>

                    <div className={styles.cardRow}>
                        <div>
                            <div className={styles.cardLabel}>Full Name</div>
                        </div>
                        <div className={styles.cardValue}>{profile?.fullName || emptyPlaceholder}</div>
                    </div>
                    <div className={styles.cardRow}>
                        <div>
                            <div className={styles.cardLabel}>Email</div>
                        </div>
                        <div className={styles.cardValue}>{profile?.email || emptyPlaceholder}</div>
                    </div>
                    <div className={styles.cardRow}>
                        <div>
                            <div className={styles.cardLabel}>Phone</div>
                        </div>
                        {/* #1220 — styled dash placeholder when phone is empty */}
                        {profile?.phoneNumber ? (
                            <div className={styles.phoneRow}>
                                <div className={styles.countryCodeBadge}>{profile?.countryCode || ''}</div>
                                <div className={styles.phoneValue}>{splitPhoneNumber(profile.phoneNumber, profile.countryCode || '').localNumber || profile.phoneNumber}</div>
                            </div>
                        ) : (
                            <div className={styles.cardValue}>{emptyPlaceholder}</div>
                        )}
                    </div>
                    <div className={styles.cardRow}>
                        <div>
                            <div className={styles.cardLabel}>Password</div>
                        </div>
                        <div className={styles.cardValue}>{'••••••••'}</div>
                    </div>
                </>
            ) : (
                <div>
                    <div className={styles.cardHeader}>
                        <div className={styles.cardTitle}>Edit Profile</div>
                    </div>
                    <div className={styles.gridInputs}>
                        {/* #1169 / #1191 / #1226 — inline errors via feedback + feedbackMessage */}
                        <InputField
                            name="fullName"
                            placeholder="John Doe"
                            label="Full Name"
                            value={form.fullName}
                            onChange={(v) => handleChange('fullName', v)}
                            disabled={saving}
                            required
                            feedback={getFieldError('fullName') ? 'error' : undefined}
                            feedbackMessage={getFieldError('fullName')}
                        />
                        <InputField
                            name="email"
                            placeholder="user@example.com"
                            label="Email"
                            value={form.email}
                            onChange={(v) => handleChange('email', v)}
                            disabled={saving}
                            type="email"
                            required
                            feedback={getFieldError('email') ? 'error' : undefined}
                            feedbackMessage={getFieldError('email')}
                        />
                        {/* #1221 — "(optional)" label for phone fields */}
                        <div className={styles.phoneInputs}>
                            <InputField
                                name="countryCode"
                                placeholder="+971"
                                label="Country code (optional)"
                                value={form.countryCode}
                                onChange={(v) => handleChange('countryCode', v)}
                                disabled={saving}
                                feedback={getFieldError('countryCode') ? 'error' : undefined}
                                feedbackMessage={getFieldError('countryCode')}
                            />
                            <InputField
                                name="phoneNumber"
                                placeholder="501234567"
                                label="Phone Number (optional)"
                                value={form.phoneNumber}
                                onChange={(v) => handleChange('phoneNumber', v)}
                                disabled={saving}
                                feedback={getFieldError('phoneNumber') ? 'error' : undefined}
                                feedbackMessage={getFieldError('phoneNumber')}
                            />
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                        <Button label={saving ? 'Saving...' : 'Save'} onClick={handleSave} variant={saving || !isFormValid() ? 'disabled' : 'primary'} disabled={saving || !isFormValid()} />
                        <Button label="Cancel" onClick={handleCancel} variant="ghost" disabled={saving} />
                    </div>
                </div>
            )}

            <div className={styles.changePasswordSection}>
                {!changePwMode ? (
                    <div className={styles.centered} style={{ marginTop: 8 }}>
                        <Button label="Change password" onClick={() => setChangePwMode(true)} variant="ghost" />
                    </div>
                ) : (
                    <div>
                        <div style={{ marginTop: 12 }}>
                            <InputField
                                name="currentPassword"
                                type="password"
                                label="Current password"
                                value={pw.currentPassword}
                                onChange={(v) => { setPw(p => ({ ...p, currentPassword: v })); setPwTouched(t => ({ ...t, currentPassword: true })); }}
                                disabled={pwSaving}
                                feedback={getPwFieldError('currentPassword') ? 'error' : undefined}
                                feedbackMessage={getPwFieldError('currentPassword')}
                            />
                            <InputField
                                name="newPassword"
                                type="password"
                                label="New password"
                                value={pw.newPassword}
                                onChange={(v) => { setPw(p => ({ ...p, newPassword: v })); setPwTouched(t => ({ ...t, newPassword: true })); }}
                                disabled={pwSaving}
                                feedback={getPwFieldError('newPassword') ? 'error' : undefined}
                                feedbackMessage={getPwFieldError('newPassword')}
                            />
                            {/* #769 — Password strength indicator */}
                            {pw.newPassword && (() => {
                                const { level } = getPasswordStrength(pw.newPassword);
                                const colorClass = strengthToColor(level);
                                return (
                                    <div className={`${styles.pwStrength} ${styles[colorClass]}`} aria-live="polite">
                                        <div className={styles.strengthBar}>
                                            <div className={`${styles.strengthFill} ${styles[colorClass]}`} style={{ width: level === 'Strong' ? '100%' : level === 'Medium' ? '66%' : '33%' }} />
                                        </div>
                                        <span className={styles.strengthLabel}>Password strength: {level}</span>
                                    </div>
                                );
                            })()}
                            <InputField
                                name="confirmPassword"
                                type="password"
                                label="Confirm password"
                                value={pw.confirmPassword}
                                onChange={(v) => { setPw(p => ({ ...p, confirmPassword: v })); setPwTouched(t => ({ ...t, confirmPassword: true })); }}
                                disabled={pwSaving}
                                feedback={getPwFieldError('confirmPassword') ? 'error' : undefined}
                                feedbackMessage={getPwFieldError('confirmPassword')}
                            />
                        </div>
                        <div className={styles.buttonRow}>
                            {
                                // enable save only when current password provided, new password meets rules and matches confirm
                            }
                            {(() => {
                                const pwValid = pw.currentPassword && validatePassword(pw.newPassword) && pw.newPassword === pw.confirmPassword;
                                return (
                                    <>
                                        <Button label={pwSaving ? 'Saving...' : 'Save password'} onClick={handlePwChange} variant={pwSaving || !pwValid ? 'disabled' : 'primary'} disabled={pwSaving || !pwValid} />
                                        <Button label="Cancel" onClick={handleCancelPasswordChange} variant="ghost" disabled={pwSaving} />
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Profile;
